"use client";

import { useEffect } from "react";
import { trackWhatsAppConversion } from "@/lib/analytics/google-ads";

/**
 * Fires a Google Ads conversion whenever a WhatsApp CTA is clicked.
 *
 * Uses one delegated listener instead of a handler on each button — the
 * wa.me links live in ~27 places (homepage CTA, pricing, footer, templates,
 * demo bar), and new ones should be counted without extra wiring.
 */
export default function WhatsAppConversion() {
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      const link = target?.closest?.("a") as HTMLAnchorElement | null;
      if (!link) return;

      const href = link.getAttribute("href") || "";
      if (!/(?:wa\.me|api\.whatsapp\.com)/i.test(href)) return;

      // New-tab links: the page survives, so fire and let the click through.
      const opensNewTab =
        link.target === "_blank" || e.metaKey || e.ctrlKey || e.button === 1;

      if (opensNewTab) {
        trackWhatsAppConversion();
        return;
      }

      // Same-tab links: hold navigation until the hit lands (max 700ms).
      e.preventDefault();
      trackWhatsAppConversion(() => {
        window.location.href = link.href;
      });
    }

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
}
